import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

const DEBOUNCE_MS = 300;

/**
 * Fetch the rendered Markdown preview of a note. Off until enabled.
 * The returned `refresh()` debounces the next fetch by 300 ms so
 * field-edit handlers can call it on every keystroke.
 */
export function useNotePreview(noteId: string, enabled: boolean): {
  markdown: string;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const fetchNow = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.get<{ markdown: string }>(`/api/notes/${noteId}/preview`);
      setMarkdown(res.markdown);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview fetch failed');
    } finally {
      setLoading(false);
    }
  }, [noteId, enabled]);

  useEffect(() => {
    if (enabled) void fetchNow();
  }, [enabled, fetchNow]);

  const refresh = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void fetchNow(); }, DEBOUNCE_MS);
  }, [fetchNow]);

  return { markdown, loading, error, refresh };
}
